import { extractQueryState } from "@shared/utils/extractQueryState";
import { useInfiniteQuery } from "@tanstack/react-query";
import { getChatApiClient } from "@shared/apis/sidekick/api-factories";
import {
  GetThreadMessagesResponse,
  Message,
} from "@shared/apis/sidekick/generated";
import { toast } from "@shared/services/toast";
import { useEffect, useMemo } from "react";
import { ChatMessage, useMessagesStore } from "@store/messages.store";
import { getAuthor } from "@features/Chat/chat-helpers";
import { useAuthStore, User } from "@store/auth.store";
import { isValidChatMessageType } from "@features/Chat/components/Message/helpers";
import { sortByCreatedAt } from "@shared/utils/sortBy";

type Props = {
  threadId: string | null | undefined;
  onSuccess?: (data: GetThreadMessagesResponse) => void;
  onError?: () => void;
};

const GET_MESSAGES_QUERY_KEY = "messages";
const CACHE_TIME_IN_MS = 1000 * 60 * 5; // 5 minutes
const PAGE_SIZE = 30;

export const buildGetMessagesKey = ({
  threadId,
}: {
  threadId: string | null;
}) => {
  return [GET_MESSAGES_QUERY_KEY, threadId];
};

const mapArtifactsToSubMessages = (
  artifact: NonNullable<Message["artifacts"]>[number],
): NonNullable<ChatMessage["subMessages"]>[number] | [] => {
  if (artifact.kind === "reasoning") {
    if (
      !("text" in artifact.content && typeof artifact.content.text === "string")
    ) {
      console.warn(`Invalid reasoning content for artifact: ${artifact.id}`);
      return [];
    }
    return {
      id: artifact.id,
      text: artifact.content.text,
      user: getAuthor({ createdBy: "bot", user: null }),
      createdAt: artifact.createdAt ? new Date(artifact.createdAt) : new Date(),
      type: "reasoning",
      createdBy: "bot",
    };
  }
  return {
    id: artifact.id,
    text: JSON.stringify(artifact.content),
    user: getAuthor({ createdBy: "bot", user: null }),
    createdAt: artifact.createdAt ? new Date(artifact.createdAt) : new Date(),
    type: "debug-json",
    createdBy: "bot",
  };
};

const parseResponseToChatMessages = (
  user: User,
  messages?: Message[],
): ChatMessage[] => {
  const parsedMessages = messages?.flatMap((message) => {
    if (message.status === "pending") return [];
    if (!isValidChatMessageType(message.type)) {
      console.warn(`Unknown message type received: ${message.type}`);
      return [];
    }

    const subMessagesFromArtifacts = !message.artifacts?.length
      ? {}
      : { subMessages: message.artifacts.flatMap(mapArtifactsToSubMessages) };

    return [
      {
        id: message.uniqueKey,
        text:
          "content" in message.data && typeof message.data.content === "string"
            ? message.data.content
            : "",
        createdAt: new Date(message.createdAt),
        type: message.type,
        user: getAuthor({ createdBy: message.createBy, user }),
        createdBy: message.createBy,
        ...subMessagesFromArtifacts,
        artifacts: message.artifacts ? sortByCreatedAt(message.artifacts) : [],
      },
    ];
  });
  return parsedMessages || [];
};

export const useGetMessages = (params: Props) => {
  const { threadId = "", onSuccess, onError } = params;
  const setMessages = useMessagesStore((state) => state.setMessages);
  const user = useAuthStore((state) => state.user);

  const chatApi = getChatApiClient();
  const query = useInfiniteQuery({
    queryKey: buildGetMessagesKey({ threadId }),
    queryFn: async ({ signal, pageParam = 1 }) => {
      const response = await chatApi.chatControllerGetThreadMessages(
        {
          thread: threadId || "",
          page: pageParam,
          pageSize: PAGE_SIZE,
        },
        { signal },
      );
      onSuccess?.(response);
      return response;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.messages.length < PAGE_SIZE) {
        return undefined;
      }
      return allPages.length + 1;
    },
    initialPageParam: 1,
    enabled: !!threadId,
    retry: 2,
    refetchInterval: (query) => {
      const latestPage = query.state.data?.pages?.[0];
      const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000);
      const inFlightMessages =
        latestPage?.messages?.filter((message: Message) => {
          return (
            message.createBy === "bot" &&
            (message.status === "pending" ||
              ("content" in message.data &&
                typeof message.data.content === "string" &&
                message.data.content === ""))
          );
        }) || [];

      if (!inFlightMessages.length) return false;

      const latestInFlight = inFlightMessages[inFlightMessages.length - 1];

      const isRecentMessage =
        new Date(latestInFlight.createdAt).getTime() > twoMinutesAgo.getTime();

      return isRecentMessage ? 3000 : false;
    },
    refetchOnWindowFocus: false,
    gcTime: CACHE_TIME_IN_MS,
    staleTime: CACHE_TIME_IN_MS,
  });

  const allMessages = useMemo(() => {
    const flattenedMessages =
      query.data?.pages?.flatMap((page) => page.messages) || [];
    if (flattenedMessages.length > 0) {
      const newMessages = parseResponseToChatMessages(user, flattenedMessages);
      return newMessages;
    }
    return [];
  }, [query.data?.pages, user]);

  const hasPendingBotMessage = useMemo(() => {
    const latestPage = query.data?.pages?.[0];
    return (
      latestPage?.messages?.some(
        (m: Message) => m.createBy === "bot" && m.status === "pending",
      ) ?? false
    );
  }, [query.data?.pages]);

  useEffect(() => {
    if (allMessages.length > 0 && threadId) {
      setMessages(threadId, allMessages);
      useMessagesStore.persist.clearStorage();
    }
  }, [allMessages, threadId, setMessages]);

  const { error } = query;
  useEffect(() => {
    if (threadId && error) {
      console.error("Failed to get messages.", error);
      toast.error(`Failed to get messages with id: ${threadId}`);
      onError?.();
    }
  }, [error, onError, threadId]);

  return {
    ...extractQueryState(query),
    messages: allMessages,
    hasPendingBotMessage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
};
